import * as React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import Dropdown from 'antd/lib/dropdown';
import Tag from 'antd/lib/tag';

import { RootState } from '../../../reducers/reducers';
import {
    selectApp,
    selectOrg,
    selectRelease,
    selectReleaseDisplayableVersion,
    selectRolloutStepRunStatusDataInStepRunGraph,
    selectIsWorkflowsMigratedAndEnabled,
    selectDestinationInfoByDestinationStepConfigId,
    selectIsDemoInstance,
} from '../../../selectors/selectors';
import {
    selectUpcomingReleases,
    selectCurrentReleases,
    selectCompletedReleases,
} from '../../../selectors/memoizedSelectors';
import { DestinationCounter } from '../../appHome/DestinationCounter';
import { Destination } from '../../../types/destinations';
import { isReleaseTypeHotfixOrRollback, statusColorForReleaseStatus } from '../../../helpers';
import { titleStyle } from '../../styles/globalStyles';
import {
    displayTextForReleaseType,
    statusStringForReleaseStatus,
    displayTextForOverviewDestinationStatus,
} from '../../../helpers/displayHelpers';
import {
    DestinationStatusDataSourceItem,
    DestinationStatusTagWithPopover,
} from '../../workflowOverview/widgets/WorkflowRunDestinationStatus';
import { ReleaseTypeTag } from '../../workflowOverview/widgets/WorkflowRunOverviewStatus';
import { ReleaseTimelinePhase } from '../../../types';
import { antGrey, runwayColors } from '../../styles/globalStyles';
import { useSidebarColors, useSidebarTheme } from '../../styles/themeMode';
import { useNavigationContext } from '../../../utils/hooks/useNavigationContext';
import { VersionDropdown } from './VersionDropdown';
import { DropdownButtonContent } from './DropdownButtonContent';

interface ReleaseHeaderSectionProps {
    orgId: string;
    appId?: string | null;
    releaseId?: string | null;
    releaseIdParam: string;
}

export const ReleaseHeaderSection: React.FC<ReleaseHeaderSectionProps> = ({
    orgId,
    appId,
    releaseId,
    releaseIdParam,
}) => {
    const org = useSelector((state: RootState) => selectOrg(state, orgId));
    const app = useSelector((state: RootState) => selectApp(state, appId));
    const isWorkflowsMigrated = useSelector((state: RootState) => selectIsWorkflowsMigratedAndEnabled(state, appId));
    const destinations = useSelector(
        (state: RootState) => Object.values(selectDestinationInfoByDestinationStepConfigId(state, appId)),
        shallowEqual,
    );

    const sidebarTheme = useSidebarTheme();
    const { orgIdParam, appIdParam } = useNavigationContext();
    const customerOrgId = org?.orgId || orgIdParam || '';
    const customerAppId = app?.appId || appIdParam || '';

    const upcomingReleases = useSelector((state: RootState) => selectUpcomingReleases(state, appId));
    const currentReleases = useSelector((state: RootState) => selectCurrentReleases(state, appId));
    const completedReleases = useSelector((state: RootState) => selectCompletedReleases(state, appId));

    const releaseFromId = useSelector((state: RootState) => (releaseId ? selectRelease(state, releaseId) : null));
    const isDemoInstance = useSelector((state: RootState) => selectIsDemoInstance(state, orgId));

    const matchesReleaseParam = React.useCallback(
        (r: { releaseId?: string; displayableVersion?: string; version?: string }) => {
            if (r.releaseId === releaseIdParam) return true;
            // Demo instance only: also match by displayableVersion/version to avoid affecting non-demo instances
            if (isDemoInstance) {
                return r.displayableVersion === releaseIdParam || r.version === releaseIdParam;
            }
            return false;
        },
        [releaseIdParam, isDemoInstance],
    );

    const currentRelease = React.useMemo(() => {
        if (releaseFromId) return releaseFromId;
        if (releaseIdParam && appId) {
            const fromList = (releases: typeof upcomingReleases) => releases.find(matchesReleaseParam);
            return fromList(upcomingReleases) || fromList(currentReleases) || fromList(completedReleases) || null;
        }
        return null;
    }, [
        releaseFromId,
        releaseIdParam,
        appId,
        upcomingReleases,
        currentReleases,
        completedReleases,
        matchesReleaseParam,
    ]);

    const currentReleaseVersion =
        useSelector((state: RootState) => (releaseId ? selectReleaseDisplayableVersion(state, releaseId) : null)) ||
        currentRelease?.displayableVersion ||
        currentRelease?.version;

    const effectiveReleaseId = releaseId || currentRelease?.id;

    const rolloutStepRunStatusDataInStepRunGraph = useSelector((state: RootState) => {
        if (!effectiveReleaseId) return [];
        const release = selectRelease(state, effectiveReleaseId);
        if (!release?.stepRunGraph?.stepGroups) return [];
        try {
            return selectRolloutStepRunStatusDataInStepRunGraph(state, effectiveReleaseId);
        } catch {
            return [];
        }
    });

    const statusDataSources: DestinationStatusDataSourceItem[] = React.useMemo(() => {
        if (!rolloutStepRunStatusDataInStepRunGraph.length) return [];
        return rolloutStepRunStatusDataInStepRunGraph
            .filter((rolloutStepRunStatusData) => !!rolloutStepRunStatusData.extraData?.destination)
            .map((rolloutStepRunStatusData) => {
                const extraData = rolloutStepRunStatusData.extraData;
                const destination = extraData?.destination as Destination;
                const status = extraData?.status;
                const phasedReleasePercentage = extraData?.phasedReleasePercentage;
                const maybeReviewStartedAt = extraData?.maybeReviewStartedAt;

                return {
                    destinationStepId: destination.stepConfigId,
                    destination,
                    status,
                    displayText: displayTextForOverviewDestinationStatus(
                        status,
                        destination.platform,
                        destination.appStoreType,
                        phasedReleasePercentage,
                    ),
                    maybeReviewStartedAt,
                };
            });
    }, [rolloutStepRunStatusDataInStepRunGraph]);

    const releaseStatus = currentRelease?.status;
    const releaseType = currentRelease?.type;
    const releaseTimelinePhase = currentRelease?.timelinePhase;

    const shouldShowDestinationStatusTag = isWorkflowsMigrated && !!effectiveReleaseId && statusDataSources.length > 1;

    const isUpcomingRelease = releaseTimelinePhase === ReleaseTimelinePhase.Upcoming;
    const isCurrentRelease = releaseTimelinePhase === ReleaseTimelinePhase.Current;
    const isSkippedRelease = releaseStatus === 'skipped';

    const releaseStatusText =
        isCurrentRelease ? 'Next release'
        : isUpcomingRelease ? 'Upcoming'
        : releaseStatus ? statusStringForReleaseStatus(releaseStatus)
        : null;
    const releaseTypeText = releaseType ? displayTextForReleaseType(releaseType) : null;

    const showReleaseStatusTag = !!currentRelease && !!releaseStatusText;
    const showReleaseTypeTag =
        !!currentRelease && !!releaseTypeText && !!releaseType && isReleaseTypeHotfixOrRollback(releaseType);

    const [isVersionDropdownOpen, setIsVersionDropdownOpen] = React.useState(false);
    const sc = useSidebarColors();
    return (
        <>
            <div style={{ borderTop: `1px solid ${sc.border}` }}>
                <Dropdown
                    open={isVersionDropdownOpen}
                    onOpenChange={setIsVersionDropdownOpen}
                    dropdownRender={() => (
                        <VersionDropdown
                            orgId={customerOrgId}
                            appId={customerAppId || ''}
                            currentReleaseId={releaseId || ''}
                            upcomingReleases={upcomingReleases}
                            currentReleases={currentReleases}
                            completedReleases={completedReleases}
                            onSelect={() => setIsVersionDropdownOpen(false)}
                        />
                    )}
                    trigger={['click']}
                >
                    <div>
                        <DropdownButtonContent
                            icon={
                                <DestinationCounter
                                    destinations={destinations}
                                    size={24}
                                    countOnly
                                    hideTooltip
                                    darkMode={sidebarTheme === 'dark'}
                                />
                            }
                            text={currentReleaseVersion || releaseIdParam}
                            showCaret
                        />
                    </div>
                </Dropdown>
            </div>

            {currentRelease && (
                <div style={{ padding: 4, display: 'flex', gap: 4, backgroundColor: sc.dropdownBg }}>
                    {showReleaseTypeTag && (
                        <ReleaseTypeTag releaseTypeText={releaseTypeText} style={{ flex: 1, textAlign: 'center' }} />
                    )}

                    {showReleaseStatusTag && (
                        <Tag
                            color={
                                isUpcomingRelease || isCurrentRelease ? undefined : (
                                    statusColorForReleaseStatus(releaseStatus)
                                )
                            }
                            style={{
                                ...titleStyle,
                                fontSize: 10,
                                lineHeight: '22px',
                                margin: 0,
                                color:
                                    isUpcomingRelease ? runwayColors.statusYellow
                                    : isCurrentRelease ? antGrey.grey10
                                    : runwayColors.white,
                                opacity: isSkippedRelease ? '60%' : '100%',
                                flex: 1,
                                textAlign: 'center',
                                ...(isUpcomingRelease && {
                                    backgroundColor: runwayColors.white,
                                }),
                                ...(isCurrentRelease && {
                                    backgroundColor: runwayColors.statusYellow,
                                    border: 'none',
                                }),
                            }}
                        >
                            {releaseStatusText}
                        </Tag>
                    )}

                    {shouldShowDestinationStatusTag && (
                        <DestinationStatusTagWithPopover
                            destinationStatusDataSourceItems={statusDataSources}
                            shouldRenderStatusData
                        />
                    )}
                </div>
            )}
        </>
    );
};
